import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estimatedOnHand, hasSyncedLine, supplierById, unitLabel } from '@d-orca/shared/client/logistics';
import { parseNumber } from '@d-orca/shared/client/utils';
import { useOrderSupplier } from '@d-orca/shared/client/hooks';
import { formatFieldDay } from '@components/ui/DateField/fieldDate';
import { useSiteDayRollover } from '../useSiteDayRollover';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { DateFieldRow } from './DeliveryOrderDateFieldRow';
import { DeliveryStatusField } from './DeliveryStatusField';
import { SimilarNameGuardModal } from './SimilarNameGuardModal';
import { rankNameOptions } from './similarMaterials';
import { useSimilarNameGuard } from './useSimilarNameGuard';
import { DeliveryLinesField } from './DeliveryLinesField';
import {
  draftsFromInitial,
  draftsToBody,
  linesValid,
  quantityOf,
  type IDeliveryLineDraft,
} from './deliveryLines';
import { SupplierField } from './SupplierField';
import { DeliveryOrderMergeBox } from './DeliveryOrderMergeBox';
import type { IDeliveryOrderFormProps } from './deliveryOrder.types';
import {
  computeSeeded,
  computeEarliest,
  isTooSoon,
  computeArriving,
  computeCanMerge,
  computeMergedTotal,
  buildSubmitPayload,
} from './deliveryOrder.logic';
import {
  EMPTY_ORDER_SUGGESTION,
  reseed,
  suggestOrder,
  type IOrderSuggestion,
} from './orderProvenance';
import { TruckIcon } from '@utils/icons';
import styles from './DeliveryOrderForm.module.css';
import { makeSeedHelpers } from './orderFormSeeds';
import { useSubmitOnce } from './useSubmitOnce';

/**
 * Create/edit form for a D-ORCA-owned delivery order — the fields, their
 * validation, and the Delivered-arrival stock merge, with no chrome of its
 * own. Two hosts render it: `DeliveryOrderDialog` (the modal, for creates and
 * prefilled "Order now" opens) and the delivery drawer's Edit tab, which is
 * why the split exists — the form is the shared thing, the surfaces differ.
 *
 * Pure form — the parent owns the mutation (`onSubmit` + `submitting`).
 * `secondaryAction` is the footer's other button: the dialog passes Cancel,
 * the drawer passes Delete.
 */
export function DeliveryOrderForm({
  suppliers,
  saveError,
  suggestions,
  materialPrefill,
  deliveries,
  openOrderNotice,
  onViewOrder,
  initial,
  boardStatuses,
  prefills,
  originNote,
  footerNote,
  unitEditable,
  stockUnitOf,
  onCountInOtherUnit,
  openStock,
  onSubmit,
  tz,
  secondaryAction,
  submitting,
}: IDeliveryOrderFormProps) {
  const { t, i18n } = useTranslation();
  const { saveLocked } = useLogisticsEditLock();
  /* Field ids are minted per instance: the form has two hosts (the order
     dialog and the delivery drawer's Edit tab), and although the drawer's
     backdrop makes mounting both at once a keyboard-only corner case,
     hardcoded ids would break every label association the moment it
     happened. */
  const fieldId = useId();
  /* "Today" in the SITE's calendar, resolved once per render so this render
     and the handler closing over it cannot straddle midnight and disagree
     about which day a suggested ETA counts from. Re-read on a timer, so a
     dialog left open across the site's midnight moves on to the new day
     instead of measuring from yesterday. */
  const today = useSiteDayRollover(tz);
  const submitOnce = useSubmitOnce();
  /**
   * What the system suggests for the ORDER — destination, supplier, ETA, note — read off
   * the SET of materials on it and re-derived on every line change (ruling
   * 2026-09-15). One material answers from its own history; several answer
   * from the last truck that carried exactly that combination, or explain why
   * they cannot. See `suggestOrder`.
   *
   * An EDIT suggests nothing at all: its values are the user's own saved
   * answers, and the too-soon check has no inferred lead to measure against.
   */
  const evaluate = (rows: IDeliveryLineDraft[]): IOrderSuggestion =>
    initial
      ? EMPTY_ORDER_SUGGESTION
      : suggestOrder({ lines: rows, deliveries: deliveries ?? [], suppliers, tz, today, lang: i18n.language, t });
  /* The materials table — one draft per row, the form's single source for
     everything that used to be three top-level fields (item, quantity, unit).
     `DeliveryLinesField` renders and edits them; this component owns the
     state so the submit can read it. */
  const [lines, setLines] = useState<IDeliveryLineDraft[]>(() =>
    draftsFromInitial(initial, prefills ?? [], unitEditable === true, stockUnitOf),
  );
  /* What the order-level fields OPEN with on a create: the suggestion for the
     rows the prefills seeded, the same rule every later row change re-applies
     — so a checkout's several materials open on the multi-material answer,
     not on the first material's. An edit's is empty: its values are saved. */
  const [opening] = useState(() => evaluate(lines));
  const [destinationName, setDestinationName] = useState(
    () => initial?.destinationName ?? opening.destinationName,
  );
  const [requestedDate, setRequestedDate] = useState(() => {
    // An edit keeps the order's own value (empty for an unsent draft — untouched).
    // Every CREATE path defaults to today, the day the request is raised: the
    // "Order now" prefill already carries it, and a blank "+ Order" now gets it
    // too rather than opening with the field empty. In the SITE's zone, matching
    // how `buildOrderPrefill` seeds the same field.
    if (initial) return initial.requestedDate ?? '';
    return lines[0]!.prefill?.requestedDate ?? today;
  });
  const [answeredDate, setAnsweredDate] = useState(() => initial?.answeredDate ?? '');
  const [dueDate, setDueDate] = useState(() => initial?.dueDate ?? opening.dueDate);
  const [note, setNote] = useState(() => initial?.deliveryOrder ?? opening.note);
  const supplier = useOrderSupplier({
    initialId: initial ? initial.supplierId : opening.supplierId || null,
    suppliers,
    saveError,
  });
  // Merge-on-arrival. Opt-in: recording an arrival and reconciling the yard are
  // two different claims, and the second one nobody has verified yet.
  const [mergeStock, setMergeStock] = useState(false);
  const [mergeRemaining, setMergeRemaining] = useState(() =>
    openStock ? String(estimatedOnHand(openStock)) : '',
  );

  const suggested = evaluate(lines);
  // What the SYSTEM put in each field — see `computeSeeded` for the reasoning
  // (suggestions only, truthiness-gated, and why the merge estimate seeds even
  // on an edit).
  const seeded = computeSeeded(initial, lines, suggested, openStock);

  const { seededCls, seedNote } = makeSeedHelpers(seeded.fields, t, styles);
  const pickedSupplierId = supplier.picked?.id ?? '';

  /**
   * Every change to the materials table, and the order-level re-seed that
   * follows it.
   *
   * The suggestions are facts about the SET, so adding or removing a material
   * can make one appear, change, or clear. What it must never do is overwrite
   * an answer the user gave: `reseed` writes only into a field still holding
   * the value we last put there, or one left empty. `suggested` is this
   * render's evaluation — exactly "what we last put there" — so no extra
   * bookkeeping is needed to know it.
   */
  const applyLines = (next: IDeliveryLineDraft[]) => {
    setLines(next);
    if (initial) return;
    const after = evaluate(next);
    setDestinationName((current) => reseed(current, suggested.destinationName, after.destinationName));
    setDueDate((current) => reseed(current, suggested.dueDate, after.dueDate));
    setNote((current) => reseed(current, suggested.note, after.note));
    supplier.offer(suggested.supplierId, after.supplierId);
  };
  /**
   * The board column this delivery sits in — the form's whole status story.
   *
   * Empty on a CREATE, where no column is offered at all: a new delivery always
   * starts in the first one, and only the server decides that. Every `Delivery`
   * carries a server-resolved `status`, so on an edit this is never guesswork.
   */
  const [status, setStatus] = useState<string>(() => initial?.status ?? '');
  /** What the trigger reads. Falls back to the first column exactly as
   *  `groupDeliveries` does, so a card whose stored id the set no longer holds
   *  names the column the board actually shows it in. */
  const selectedStatus =
    boardStatuses.find((s) => s.id === status) ?? boardStatuses[0] ?? null;
  /* The one-unsynced-line rule, asked per row: any material already open
     elsewhere blocks the save, exactly as the backend's 409 would. */
  const blockedLine = lines.find((line) => openOrderNotice?.(line.itemName.trim()) != null) ?? null;
  /* The save-time similar-name confirmation (design 2026-08-31): the FIRST row
     whose typed material is a near-miss of a known one. Creates only —
     renaming a line on an existing order is deliberate. */
  const guardLine = lines
    .map((line) => ({ line, match: rankNameOptions(line.itemName.trim(), suggestions).closeMatch }))
    .find((candidate) => candidate.match !== null) ?? null;

  /* The arrival merge is a ONE-MATERIAL move: it adds what landed to that
     material's running stock. A truck carrying three lands three materials,
     and picking one of them to merge would be arbitrary — those are
     reconciled from each Inventory row's own Sync instead. */
  const soleLine = lines.length === 1 ? lines[0]! : null;
  const soleQuantity = soleLine ? quantityOf(soleLine.quantity) : null;
  const hasAmount = soleLine !== null && soleQuantity != null && soleLine.unit !== '';

  // The soonest an as-yet-unmet due date could land, measured in the SITE's
  // calendar — see `computeEarliest` / `isTooSoon` for why that has to be
  // `tz`-aware rather than `.toISOString()`.
  const lead = suggested.leadTimeDays;
  const earliest = computeEarliest(tz, lead);
  const tooSoon = (value: string): boolean => isTooSoon(value, earliest);

  // The arrival: this delivery is being MOVED into Delivered and has not
  // landed before — see `computeArriving` for why all three clauses matter.
  const arriving = computeArriving(initial, status);
  // The merge is offered only on that transition, and only while the running
  // stock still has cover. A row that has already run out is a Renew, not a
  // merge — there is nothing left to add the delivery to.
  const canMerge = computeCanMerge(arriving, openStock, hasAmount, soleLine?.unit ?? '');
  const remRaw = mergeRemaining.trim().normalize('NFKC');
  const remNum = parseNumber(remRaw);
  // Blank is unanswered, not zero — `Number('')` is 0, which would silently
  // claim the old stock was entirely used up.
  const remValid = remRaw !== '' && Number.isFinite(remNum) && remNum >= 0;
  const mergedTotal = computeMergedTotal(canMerge, remValid, remNum, soleQuantity ?? 0);

  const valid =
    destinationName.trim().length > 0 &&
    linesValid(lines) &&
    blockedLine == null &&
    (!(canMerge && mergeStock) || remValid);

  const submit = () => submitOnce(() => {
    const { payload, merge } = buildSubmitPayload({
      lines: draftsToBody(lines), destinationName, note,
      requestedDate, answeredDate, dueDate, initial, status,
      canMerge, mergeStock, remNum,
      supplier: supplier.choice,
    });
    if (merge) onSubmit(payload, merge);
    else onSubmit(payload);
  });

  /** Puts a known spelling on the guarded row — the one the modal is about. */
  const useExistingName = (name: string) => {
    setLines((current) =>
      current.map((line) => (line.key === guardLine?.line.key ? { ...line, itemName: name } : line)),
    );
  };

  /* Save-time confirmation for a CREATE whose item closely matches a known
     name (design 2026-08-31, amended: a modal like the delete confirmation).
     Edits keep their name unguarded — renames are deliberate. */
  const guard = useSimilarNameGuard({
    query: guardLine?.line.itemName.trim() ?? '',
    closeMatch: guardLine?.match ?? null,
    isNewMaterial: initial == null,
    onUseExisting: useExistingName,
    onSave: submit,
  });

  const datePlaceholder = t('logistics.orderDialog.datePlaceholder');
  const clearLabel = (key: 'requested' | 'answered' | 'due') =>
    t('logistics.orderDialog.clearDate', { field: t(`logistics.orderDialog.${key}`) });

  const pickStatus = (next: string) => setStatus(next);

  return (
    <>
          {originNote && <div className={styles.origin}>{originNote}</div>}
          {/* What the truck carries. One row per material (design 2026-09-14
              §4.1) — the material search, its amount, its unit, remove — with
              "+ Add material" under them. Every per-material rule lives in
              there: the unit lock, the one-open notice, the similar-name
              nudge, the duplicate guard. */}
          <DeliveryLinesField
            fieldId={fieldId}
            lines={lines}
            onChange={applyLines}
            suggestions={suggestions}
            materialPrefill={materialPrefill}
            stockUnitOf={stockUnitOf}
            onCountInOtherUnit={onCountInOtherUnit}
            openOrderNotice={openOrderNotice}
            onViewOrder={onViewOrder}
            isEdit={initial != null}
            seededQuantities={seeded.lines}
            /* Per line, from that line's OWN prefill: a truck carrying three
               materials had two of its three amounts worked out in silence
               while only the first explained itself. An amount with no level
               behind it (lead + rate) has no figure to explain, so it says
               nothing rather than inventing one. */
            quantityHint={(line) =>
              line.prefill?.leadTimeDays != null && line.prefill.dailyRate ? (
                <span className={styles.seedHint}>
                  {t('logistics.orderDialog.quantityWhy', {
                    rate: line.prefill.dailyRate,
                    unit: unitLabel(t, line.unit),
                    lead: line.prefill.leadTimeDays,
                  })}
                </span>
              ) : null
            }
          />

          <SupplierField
            fieldId={fieldId}
            suppliers={suppliers}
            supplier={supplier}
            className={seededCls('supplierId', pickedSupplierId)}
            footnote={
              <>
                {/* As the destination's: the reason rides with the value. */}
                {pickedSupplierId === suggested.supplierId && suggested.supplierWhy !== '' ? (
                  <span className={styles.seedHint}>{suggested.supplierWhy}</span>
                ) : null}
                {seedNote('supplierId', pickedSupplierId, false, (id) => supplierById(suppliers, id)?.name ?? '')}
              </>
            }
          />

          <div className={styles.field} data-tour="logistics-order-destination">
            <label className={`${styles.label} ${styles.required}`} htmlFor={`${fieldId}-destination`}>
              {t('logistics.orderDialog.destination')}
            </label>
            <input
              id={`${fieldId}-destination`}
              aria-required="true"
              className={`${styles.input}${seededCls('destinationName', destinationName)}`}
              value={destinationName}
              onChange={(e) => setDestinationName(e.target.value)}
              maxLength={120}
              placeholder={t('logistics.orderDialog.destinationExample')}
              autoComplete="off"
            />
            {/* The reason rides with the value, not with the amber mark: with
                several materials the honest suggestion is often NO destination,
                and that blank is exactly the case that has to explain itself.
                It goes once the user types their own answer. */}
            {destinationName === suggested.destinationName && suggested.destinationWhy !== '' ? (
              <span className={styles.seedHint}>{suggested.destinationWhy}</span>
            ) : null}
            {seedNote('destinationName', destinationName)}
            {/* Required, and said so: an empty destination is a reason the
                save button is off, and a reason nobody can see is no reason. */}
            {destinationName.trim() === '' ? (
              <span className={styles.fieldHint}>{t('logistics.orderDialog.destinationRequired')}</span>
            ) : null}
          </div>

          {/* The three workflow dates side by side, on the shared ui/DateField
              calendar the analytics cards and the issues dialog already use. */}
          <div className={styles.rowFields} data-tour="logistics-order-dates">
            <DateFieldRow
              label={t('logistics.orderDialog.requested')}
              placeholder={datePlaceholder}
              today={today}
              clearLabel={clearLabel('requested')}
              value={requestedDate}
              /* Clearing the request unwinds what it made possible. Leaving the
                 later dates behind would store the exact state the two locks
                 below exist to prevent — an answer to a request that was never
                 made — with nothing on screen admitting it. */
              onChange={(next) => {
                setRequestedDate(next);
                if (!next) {
                  setAnsweredDate('');
                  setDueDate('');
                }
              }}
            />
            {/* The dates are a chain: a supplier cannot answer a request that
                was never sent, and a deadline cannot be agreed before either.
                Both stay locked until the request has a date, so the invalid
                order is unreachable rather than merely discouraged. */}
            <DateFieldRow
              label={t('logistics.orderDialog.answered')}
              placeholder={datePlaceholder}
              today={today}
              clearLabel={clearLabel('answered')}
              value={answeredDate}
              onChange={setAnsweredDate}
              disabled={!requestedDate}
              note={
                !requestedDate ? (
                  <span className={styles.fieldHint}>
                    {t('logistics.orderDialog.needsRequested')}
                  </span>
                ) : null
              }
            />
            <DateFieldRow
              label={t('logistics.orderDialog.due')}
              placeholder={datePlaceholder}
              today={today}
              clearLabel={clearLabel('due')}
              value={dueDate}
              onChange={setDueDate}
              disabled={!requestedDate}
              marked={seededCls('dueDate', dueDate) !== ''}
              note={
                <>
                  {tooSoon(dueDate) ? (
                    <span className={styles.fieldWarn}>
                      {t('logistics.orderDialog.tooSoon', { lead, earliest: formatFieldDay(earliest!) })}
                    </span>
                  ) : dueDate === suggested.dueDate && suggested.dueWhy !== '' ? (
                    <span className={styles.seedHint}>{suggested.dueWhy}</span>
                  ) : null}
                  {seedNote('dueDate', dueDate, false, formatFieldDay)}
                </>
              }
            />
          </div>

          <div className={styles.field} data-tour="logistics-order-note">
            <label className={styles.label} htmlFor={`${fieldId}-note`}>
              {t('logistics.orderDialog.note')}
            </label>
            <textarea
              id={`${fieldId}-note`}
              className={`${styles.input} ${styles.note}${seededCls('note', note)}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
            {note === suggested.note && suggested.noteWhy !== '' ? (
              <span className={styles.seedHint}>{suggested.noteWhy}</span>
            ) : null}
            {seedNote('note', note)}
          </div>

          {/* Edit only. A new delivery has no column to choose: it starts in the
              first one, and offering the choice would invite someone to file a
              request that has not been raised yet as already arrived. */}
          {initial ? (
            <DeliveryStatusField
              fieldId={fieldId}
              /* ANY synced line freezes the column, not only a fully synced
                 order — the server refuses the move either way. */
              locked={hasSyncedLine(initial)}
              selectedStatus={selectedStatus}
              boardStatuses={boardStatuses}
              onPick={pickStatus}
              t={t}
            />
          ) : null}


          {/* Marking a delivery arrived means both amounts are now on site.
              Offering the merge here saves the trip to the inventory row —
              recording the arrival and reconciling the stock are one motion.
              Opt-in, because they are two different claims: the arrival is a
              fact, what is left on the floor is a count nobody has taken yet. */}
          {canMerge ? (
            <DeliveryOrderMergeBox
              openStock={openStock!}
              tz={tz}
              mergeStock={mergeStock}
              setMergeStock={setMergeStock}
              mergeRemaining={mergeRemaining}
              setMergeRemaining={setMergeRemaining}
              seededCls={seededCls}
              seedNote={seedNote}
              qtyNum={soleQuantity ?? 0}
              remValid={remValid}
              remNum={remNum}
              mergedTotal={mergedTotal}
            />
          ) : null}

          <SimilarNameGuardModal
            guard={guard}
            typed={guardLine?.line.itemName.trim() ?? ''}
            body={t('logistics.similarName.bodyDelivery', {
              typed: guardLine?.line.itemName.trim() ?? '',
              existing: guard.match ?? '',
            })}
            t={t}
          />
          {/* What Create does besides this order — the Stock tab checkout's
              adds to orders already on their way — said where the click is. */}
          {footerNote ? (
            <div className={styles.footerNote}>
              <TruckIcon size={14} />
              <span>{footerNote}</span>
            </div>
          ) : null}
          {/* A supplier draft saves with the order, but not without a Company. */}
          {supplier.blocksSave ? (
            <div className={styles.footerNote} role="status">{t('logistics.orderDialog.supplierPending')}</div>
          ) : null}
          <div className={styles.footer}>
            {secondaryAction}
            <button
              type="button"
              className={styles.saveBtn}
              disabled={!valid || submitting || saveLocked || supplier.blocksSave}
              onClick={guard.onSaveClick}
            >
              {/* The write is a real round trip (two, when a merge rides
                  along), and the host only closes on success — the label is
                  the feedback that the click landed. */}
              {submitting
                ? t(initial ? 'logistics.orderDialog.saving' : 'logistics.orderDialog.creating')
                : t(initial ? 'logistics.orderDialog.save' : 'logistics.orderDialog.create')}
            </button>
          </div>
    </>
  );
}
